import React, { createContext, useState, useContext, useCallback } from "react";
import { roomService } from "../services/rooms";
import { toast } from "react-toastify";

const RoomContext = createContext();

export const useRoom = () => {
  const context = useContext(RoomContext);
  if (!context) {
    throw new Error("useRoom must be used within a RoomProvider");
  }
  return context;
};

export const RoomProvider = ({ children }) => {
  const [rooms, setRooms] = useState([]);
  const [myRooms, setMyRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all available fields and subfields
   */
  const fetchFields = useCallback(async () => {
    try {
      const response = await roomService.getResearchFields();
      if (response.success) {
        setFields(response.data);
      }
    } catch (err) {
setError(err.message);
    }
  }, []);

  /**
   * Fetch all rooms with optional filters
   */
  const fetchRooms = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomService.getAllRooms(filters);

      if (response.success) {
        setRooms(response.data || []);
        return response;
      }
    } catch (err) {
setError(err.message);
      toast.error("Failed to load labs");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch user's joined rooms
   */
  const fetchMyRooms = useCallback(async (role = "all") => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomService.getMyRooms(role);

      if (response.success) {
        setMyRooms(response.data || []);
        return response;
      }
    } catch (err) {
setError(err.message);
      toast.error("Failed to load your labs");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch a specific room by ID
   */
  const fetchRoomById = useCallback(async (roomId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomService.getRoomById(roomId);

      if (response.success) {
        setCurrentRoom(response.data);
        return response.data;
      }
    } catch (err) {
setError(err.message);
      toast.error("Failed to load lab details");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new room
   */
  const createRoom = useCallback(async (roomData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomService.createRoom(roomData);

      if (response.success) {
        toast.success("Lab created successfully!");
        // Add to myRooms
        setMyRooms((prev) => [response.data, ...prev]);
        return response.data;
      }
    } catch (err) {
setError(err.message);
      toast.error(err.message || "Failed to create lab");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Request to join a room
   */
  const joinRoom = useCallback(async (roomId, message = "") => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomService.requestToJoinRoom(roomId, message);

      if (response.success) {
        toast.success("Join request sent successfully!");
        return response;
      }
    } catch (err) {
setError(err.message);
      toast.error(err.message || "Failed to send join request");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Leave a room
   */
  const leaveRoom = useCallback(async (roomId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomService.leaveRoom(roomId);

      if (response.success) {
        toast.success("You have left the lab");
        // Remove from myRooms
        setMyRooms((prev) => prev.filter((room) => room._id !== roomId));
        return response;
      }
    } catch (err) {
setError(err.message);
      toast.error(err.message || "Failed to leave lab");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Handle join request (Leader only)
   */
  const handleJoinRequest = useCallback(
    async (roomId, userId, action) => {
      try {
        setLoading(true);
        setError(null);
        const response = await roomService.handleJoinRequest(
          roomId,
          userId,
          action
        );

        if (response.success) {
          toast.success(
            action === "accept" ? "Member added to lab" : "Request rejected"
          );
          // Refresh current room data
          if (currentRoom && currentRoom._id === roomId) {
            await fetchRoomById(roomId);
          }
          return response;
        }
      } catch (err) {
setError(err.message);
        toast.error(err.message || "Failed to handle request");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [currentRoom, fetchRoomById]
  );

  /**
   * Remove a member (Leader only)
   */
  const removeMember = useCallback(
    async (roomId, userId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await roomService.removeMember(roomId, userId);

        if (response.success) {
          toast.success("Member removed successfully");
          // Refresh current room data
          if (currentRoom && currentRoom._id === roomId) {
            await fetchRoomById(roomId);
          }
          return response;
        }
      } catch (err) {
setError(err.message);
        toast.error(err.message || "Failed to remove member");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [currentRoom, fetchRoomById]
  );

  /**
   * Get user's role in a room
   */
  const getUserRoleInRoom = useCallback((room, userId) => {
    if (!room || !userId) return null;

    // Check if user is the leader
    const leaderId = room.leader?._id || room.leader?.id || room.leader;
    if (leaderId === userId) {
      return { isLeader: true, isMember: true, role: "leader" };
    }

    // Check if user is a member
    const member = room.members?.find(
      (m) => (m.user?._id || m.user?.id || m.user) === userId
    );

    if (member) {
      return { isLeader: false, isMember: true, role: member.role || "member" };
    }

    return { isLeader: false, isMember: false, role: null };
  }, []);

  /**
   * Update room settings (Leader only)
   */
  const updateRoom = useCallback(
    async (roomId, updates) => {
      try {
        setLoading(true);
        setError(null);
        const response = await roomService.updateRoom(roomId, updates);

        if (response.success) {
          toast.success("Lab updated successfully");
          // Update current room data
          if (currentRoom && currentRoom._id === roomId) {
            setCurrentRoom(response.data);
          }
          // Update in myRooms list
          setMyRooms((prev) =>
            prev.map((room) =>
              room._id === roomId ? { ...room, ...response.data } : room
            )
          );
          return response;
        }
      } catch (err) {
setError(err.message);
        toast.error(err.message || "Failed to update lab");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [currentRoom]
  );

  /**
   * Delete room (Leader only)
   */
  const deleteRoom = useCallback(
    async (roomId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await roomService.deleteRoom(roomId);

        if (response.success) {
          toast.success("Lab deleted successfully");
          // Remove from myRooms
          setMyRooms((prev) => prev.filter((room) => room._id !== roomId));
          // Remove from rooms list
          setRooms((prev) => prev.filter((room) => room._id !== roomId));
          // Clear current room if it was the deleted one
          if (currentRoom && currentRoom._id === roomId) {
            setCurrentRoom(null);
          }
          return response;
        }
      } catch (err) {
setError(err.message);
        toast.error(err.message || "Failed to delete room");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [currentRoom]
  );

  const value = {
    // State
    rooms,
    myRooms,
    currentRoom,
    fields,
    loading,
    error,

    // Actions
    fetchFields,
    fetchRooms,
    fetchMyRooms,
    fetchRoomById,
    createRoom,
    joinRoom,
    leaveRoom,
    handleJoinRequest,
    removeMember,
    updateRoom,
    deleteRoom,
    getUserRoleInRoom,

    // Setters (for direct updates if needed)
    setCurrentRoom,
    setRooms,
    setMyRooms,
  };

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
};

export default RoomContext;
